import React, { useEffect, useRef, useState } from "react";
import DatePicker from "react-datepicker";
import swal from "sweetalert";

const CustomDatePicker = ({
  index, // For Payment list, to remove calendar icon
  arrayOfObjectIndex, // To Set value of specific Index for Array of objects
  label, // Input field label
  field, // Key name from data object and used as form field name in useForm
  selected, // Date value
  handleDateChange,
  handleBlur,
  setter, // React state setter for today's date
  CustomInput,
  disabled,
  isRequired,
  validated, // Input field validation
  confirmation, // Apply custom border style when validation fails
  dateValidation, // Validation for cutoff date for "handleResetToToday" function
  postedCutoffValidation, // Posted cutoff validation for "handleResetToToday" function
  setIsAmountDisabled, // To Enable Amount input field in payment method
  iconTopOffset, // Icon position
  padding, // Input field padding
  maxDate,
  minDate,
  dateAndTime,
}) => {
  const [hoverYear, setHoverYear] = useState(null);
  const [selectYear, setSelectYear] = useState(false);
  const [yearList, setYearList] = useState([]);
  const yearListRef = useRef();
  const dateFormat = dateAndTime ? "MMM/dd/yyyy h:mm aa" : "MMM/dd/yyyy";

  // Initialize yearList
  const generateYears = (year) => {
    setYearList(() => {
      const updated = [];
      const currentYear = new Date().getFullYear();

      // Generate years based on current year or selected year
      const yearList = Array.from(
        { length: 16 },
        (_, i) => (year ? year : currentYear) - 16 + i + 1,
      );

      // Divide array by 4 items
      for (let i = 0; i < yearList.length; i += 4) {
        updated.push(yearList.slice(i, i + 4));
      }

      return updated;
    });
  };

  const increaseYear = (changeYear) => {
    setYearList((prev) => {
      const updated = prev.map((item) => item.map((year) => year + 10));
      changeYear(updated[3][3]);

      return updated;
    });
  };

  const decreaseYear = (changeYear) => {
    setYearList((prev) => {
      const updated = prev.map((item) => item.map((year) => year - 10));
      changeYear(updated[3][3]);

      return updated;
    });
  };

  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const updateValue = (today) => {
    // useForm setter
    if (setter.name === "setValue") {
      setter(field, today);
      return;
    }

    // useState: array of objects
    if (field && typeof arrayOfObjectIndex === "number") {
      setter((prev) => {
        const updated = [...prev];
        updated[arrayOfObjectIndex] = {
          ...updated[arrayOfObjectIndex],
          [field]: today,
        };

        return updated;
      });

      return;
    }

    // useState: object
    if (field) {
      setter((prev) => ({
        ...prev,
        [field]: today,
      }));

      return;
    }

    // useState: primitive
    setter(today);
  };

  // prettier-ignore
  const handleResetToToday = (changeYear, changeMonth) => {
    const today = new Date();
    const currentYear = today.getFullYear();
    const currentMonthIndex = today.getMonth();

    // Run cutoff date validation if provided
    const existingCutoff = dateValidation && dateValidation(today, setter, label, field); // Check if there's existing cutoff for the selected date
    const isPosted = postedCutoffValidation && postedCutoffValidation(today, setter, label); // Check if the selected date falls within the posted cutoff period

    if (existingCutoff === false || isPosted) return

    // Update form/state with today's date
    updateValue(today);

    // Reset supporting dropdowns (years + month selector)
    generateYears();
    changeYear(currentYear);
    changeMonth(currentMonthIndex);

    setIsAmountDisabled && setIsAmountDisabled(false); // For Payment
  };

  // Handles "Today" button click or pressing Enter
  const handleClickToday = (changeYear, changeMonth) => {
    if (label === "Birthday") {
      swal({
        icon: "warning",
        title: "Invalid Birth Date",
        text: "Birth date cannot be today's date. Please select a valid past date.",
      });

      return;
    }

    setSelectYear(false);
    handleResetToToday(changeYear, changeMonth);
  };

  useEffect(() => {
    // Initialize Year List
    generateYears();

    // Close year dropdown when clicking outside
    const handleClickOutside = (e) => {
      if (yearListRef.current && !yearListRef.current.contains(e.target))
        setSelectYear(false);
    };

    document.addEventListener("mousedown", handleClickOutside);

    // Cleanup on unmount
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div
      className={`${index === undefined && "position-relative"} flex-grow-1`}
    >
      {/* Ensures the calendar popup always renders above any sibling
          fields/rows below it (e.g. Cheque Number, Amount), instead of
          relying on every neighboring field remembering to add a z-0
          utility class.
          Keep this BELOW the sticky navbar (Bootstrap .sticky-top = 1020).
          If it is higher, an open calendar floats over the navbar when the
          page is scrolled. Calendars inside modals are not affected: the
          modal (1055) is its own stacking context, so this value only has
          to beat the fields inside it. */}
      <style>{`
        .react-datepicker-popper {
          z-index: 1010 !important;
        }
      `}</style>
      <DatePicker
        onMonthChange={(newDate) => {
          const selectedYear = new Date(newDate).getFullYear();

          const isNotInCurrentYearList = !yearList
            .flat()
            .includes(selectedYear);

          if (isNotInCurrentYearList) {
            generateYears(selectedYear);
          }
        }}
        // Dark mode: the --dm-* variables only exist under [data-theme="dark"]
        // (style.css), so in light mode every var() in this header falls back
        // to the original light colour (the second value). Keep it that way.
        renderCustomHeader={({
          date,
          changeYear,
          changeMonth,
          decreaseMonth,
          increaseMonth,
          prevMonthButtonDisabled,
          nextMonthButtonDisabled,
        }) => (
          <div
            style={{
              margin: 10,
              display: "flex",
              justifyContent: "center",
            }}
          >
            {/* Go to Previous Month */}
            <button
              onClick={decreaseMonth}
              type="button"
              className="btn py-0 me-1 rounded-0 border border-0"
              style={{ color: "var(--dm-text, #212529)" }}
              disabled={prevMonthButtonDisabled}
            >
              {"<"}
            </button>

            {/* List of Months options */}
            <select
              value={months[date?.getMonth()]}
              onChange={({ target: { value } }) =>
                changeMonth(months.indexOf(value))
              }
              className="fw-bold rounded custom-date-picker"
              style={{
                cursor: "pointer",
                backgroundColor: "transparent",
                color: "var(--dm-text, #000)",
              }}
            >
              {months.map((option) => (
                <option
                  style={{
                    backgroundColor: "var(--dm-surface-2, white)",
                    color: "var(--dm-text, #000)",
                  }}
                  key={option}
                  value={option}
                >
                  {option}
                </option>
              ))}
            </select>

            {/* List of Years options */}
            <div ref={yearListRef} className="position-relative">
              <select
                value={date?.getFullYear()}
                onMouseDown={(e) => {
                  e.preventDefault();
                  setSelectYear((prev) => !prev);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    setSelectYear((prev) => !prev);
                  }
                }}
                style={{
                  cursor: "pointer",
                  color: "var(--dm-text, #000)",
                }}
                className="h-100 fw-bold rounded bg-transparent"
              >
                {yearList.map((item) =>
                  item.map((option) => (
                    <option
                      key={option}
                      value={option}
                      style={{
                        backgroundColor: "var(--dm-surface-2, white)",
                        color: "var(--dm-text, #000)",
                      }}
                    >
                      {option}
                    </option>
                  )),
                )}
              </select>

              {selectYear && (
                <>
                  <div
                    className="position-absolute rounded"
                    style={{
                      border: "1px solid var(--dm-border, #aeaeae)",
                      backgroundColor: "var(--dm-surface, white)",
                      color: "var(--dm-text, #000)",
                    }}
                  >
                    <div
                      className="d-flex justify-content-between align-items-center p-2 mb-2"
                      style={{
                        backgroundColor: "var(--dm-surface-2, #f0f0f0)",
                        borderBottom: "1px solid var(--dm-border, #aeaeae)",
                        borderTopRightRadius: "0.3rem",
                        borderTopLeftRadius: "0.3rem",
                      }}
                    >
                      {/* Go to Previous Year */}
                      <button
                        onClick={() => decreaseYear(changeYear)}
                        type="button"
                        className="btn py-0 me-1 rounded-0 border border-0"
                        style={{ color: "var(--dm-text, #212529)" }}
                      >
                        {"<"}
                      </button>

                      <div>
                        {yearList[0][0]} {"-"}{" "}
                        {yearList[yearList.length - 1][3]}
                      </div>

                      {/* Go to Next Year */}
                      <button
                        onClick={() => increaseYear(changeYear)}
                        type="button"
                        className="btn py-0 me-1 rounded-0 border border-0"
                        style={{ color: "var(--dm-text, #212529)" }}
                      >
                        {">"}
                      </button>
                    </div>
                    <div className="px-2">
                      {yearList.map((item) => (
                        <div className="d-flex flex-column">
                          <ul className="d-flex p-0 m-0">
                            {item.map((year, i) => {
                              // Check if the current year is the selected one
                              const isSelectedYear =
                                date?.getFullYear() === year;

                              // Determine background color based on state
                              const getBgColor = () => {
                                if (isSelectedYear) return "#216ba5"; // Blue
                                if (hoverYear === year)
                                  return "var(--dm-surface-2, #f0f0f0)"; // Light gray
                                return "var(--dm-surface, white)"; // Default
                              };

                              return (
                                <li
                                  className="list-unstyled p-2 w-50"
                                  style={{
                                    cursor: "pointer",
                                    backgroundColor: getBgColor(),
                                    color: isSelectedYear
                                      ? "white"
                                      : "var(--dm-text, black)",
                                    borderRadius: "0.3rem",
                                  }}
                                  onClick={() => {
                                    changeYear(year);
                                    setSelectYear(false);
                                  }}
                                  onMouseEnter={() => setHoverYear(year)}
                                  onMouseLeave={() => setHoverYear(null)}
                                >
                                  {year}
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                      ))}
                    </div>
                    <span
                      role="button"
                      tabIndex={0}
                      type="button"
                      className="mt-2 px-2 pb-2 text-secondary"
                      style={{ float: "right" }}
                      onClick={() => handleClickToday(changeYear, changeMonth)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          handleClickToday(changeYear, changeMonth);
                        }
                      }}
                    >
                      Today
                    </span>
                  </div>
                </>
              )}
            </div>

            {/* Go to Next Month */}
            <button
              onClick={increaseMonth}
              type="button"
              className="btn py-0 ms-1 rounded-0 border border-0"
              style={{ color: "var(--dm-text, #212529)" }}
              disabled={nextMonthButtonDisabled}
            >
              {">"}
            </button>
          </div>
        )}
        selected={selected}
        dateFormat={dateFormat}
        onChange={handleDateChange}
        onCalendarClose={handleBlur || undefined}
        maxDate={label === "Birthday" ? new Date() : maxDate}
        minDate={minDate}
        {...(dateAndTime && {
          showTimeInput: true,
          timeInputLabel: "Time:",
        })}
        customInput={
          <CustomInput
            disabled={disabled}
            isRequired={isRequired}
            index={index}
            confirmation={confirmation}
            generateYears={generateYears}
            label={label}
            padding={padding}
          />
        }
        disabled={disabled}
      />
      {/* Calendar Icon */}
      <i
        className={`fa-solid fa-calendar-week calendar-position ${
          index !== undefined && "d-none"
        }`}
        style={{
          right: `${validated ? "2rem" : "1rem"}`,
          top: iconTopOffset ?? "0.8rem",
        }}
      ></i>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Calendar header with a month list and a year grid.
//
// This is the same header CustomDatePicker renders, exported on its own for a
// plain react-datepicker that needs props CustomDatePicker does not pass
// through (e.g. typed dates with onChangeRaw / strictParsing):
//
//   <DatePicker
//     renderCustomHeader={(headerProps) => (
//       <CustomDatePickerHeader {...headerProps} onToday={handleDateChange} />
//     )}
//   />
//
// - the arrows move one month; the month list jumps to any month
// - the year button opens a grid of 16 years (ending at the year shown); the
//   grid's arrows move 10 years and clicking outside of it closes it
// - "Today" in the grid moves the calendar to the current month and, when
//   onToday is given, calls it with today's date so the caller can validate
//   and select it
// Colours use the --dm-* dark-mode variables and fall back to the light theme.
// ---------------------------------------------------------------------------
const HEADER_MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

// 16 years in 4 rows of 4, ending at lastYear (the same range that
// generateYears builds inside CustomDatePicker).
const buildYearRows = (lastYear) => {
  const end = lastYear || new Date().getFullYear();

  return [0, 1, 2, 3].map((row) =>
    [0, 1, 2, 3].map((col) => end - 15 + row * 4 + col),
  );
};

export const CustomDatePickerHeader = ({
  date,
  changeYear,
  changeMonth,
  decreaseMonth,
  increaseMonth,
  prevMonthButtonDisabled,
  nextMonthButtonDisabled,
  onToday, // Optional: called with today's date when "Today" is clicked
}) => {
  const shownYear = date?.getFullYear();
  const [yearList, setYearList] = useState(() => buildYearRows(shownYear));
  const [selectYear, setSelectYear] = useState(false);
  const [hoverYear, setHoverYear] = useState(null);
  const yearListRef = useRef(null);

  // The arrows / month list can move the calendar into a year the grid does
  // not list yet (December -> January): rebuild the grid around that year.
  if (shownYear && !yearList.flat().includes(shownYear)) {
    setYearList(buildYearRows(shownYear));
  }

  // Close the year grid when clicking outside of it
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (yearListRef.current && !yearListRef.current.contains(e.target)) {
        setSelectYear(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Move the grid by 10 years and show the last year of the new range
  const shiftYears = (step) => {
    const updated = yearList.map((row) => row.map((year) => year + step));

    setYearList(updated);
    changeYear(updated[3][3]);
  };

  const handleToday = () => {
    const today = new Date();

    setSelectYear(false);
    if (onToday) onToday(today);

    setYearList(buildYearRows(today.getFullYear()));
    changeYear(today.getFullYear());
    changeMonth(today.getMonth());
  };

  return (
    <div style={{ margin: 10, display: "flex", justifyContent: "center" }}>
      {/* Go to Previous Month */}
      <button
        onClick={decreaseMonth}
        type="button"
        className="btn py-0 me-1 rounded-0 border border-0"
        style={{ color: "var(--dm-text, #212529)" }}
        disabled={prevMonthButtonDisabled}
      >
        {"<"}
      </button>

      {/* List of Months options */}
      <select
        value={HEADER_MONTHS[date?.getMonth()]}
        onChange={({ target: { value } }) =>
          changeMonth(HEADER_MONTHS.indexOf(value))
        }
        className="fw-bold rounded custom-date-picker"
        style={{
          cursor: "pointer",
          backgroundColor: "transparent",
          color: "var(--dm-text, #000)",
        }}
      >
        {HEADER_MONTHS.map((option) => (
          <option
            style={{
              backgroundColor: "var(--dm-surface-2, white)",
              color: "var(--dm-text, #000)",
            }}
            key={option}
            value={option}
          >
            {option}
          </option>
        ))}
      </select>

      {/* Year: clicking it opens the year grid */}
      <div ref={yearListRef} className="position-relative">
        <select
          value={shownYear}
          onChange={(e) => changeYear(Number(e.target.value))}
          onMouseDown={(e) => {
            e.preventDefault();
            setSelectYear((prev) => !prev);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              setSelectYear((prev) => !prev);
            }
          }}
          style={{
            cursor: "pointer",
            color: "var(--dm-text, #000)",
          }}
          className="h-100 fw-bold rounded bg-transparent"
        >
          {yearList.map((row) =>
            row.map((option) => (
              <option
                key={option}
                value={option}
                style={{
                  backgroundColor: "var(--dm-surface-2, white)",
                  color: "var(--dm-text, #000)",
                }}
              >
                {option}
              </option>
            )),
          )}
        </select>

        {selectYear && (
          <div
            className="position-absolute rounded"
            style={{
              border: "1px solid var(--dm-border, #aeaeae)",
              backgroundColor: "var(--dm-surface, white)",
              color: "var(--dm-text, #000)",
            }}
          >
            <div
              className="d-flex justify-content-between align-items-center p-2 mb-2"
              style={{
                backgroundColor: "var(--dm-surface-2, #f0f0f0)",
                borderBottom: "1px solid var(--dm-border, #aeaeae)",
                borderTopRightRadius: "0.3rem",
                borderTopLeftRadius: "0.3rem",
              }}
            >
              {/* Go to Previous 10 Years */}
              <button
                onClick={() => shiftYears(-10)}
                type="button"
                className="btn py-0 me-1 rounded-0 border border-0"
                style={{ color: "var(--dm-text, #212529)" }}
              >
                {"<"}
              </button>

              <div>
                {yearList[0][0]} {"-"} {yearList[3][3]}
              </div>

              {/* Go to Next 10 Years */}
              <button
                onClick={() => shiftYears(10)}
                type="button"
                className="btn py-0 me-1 rounded-0 border border-0"
                style={{ color: "var(--dm-text, #212529)" }}
              >
                {">"}
              </button>
            </div>

            <div className="px-2">
              {yearList.map((row) => (
                <div className="d-flex flex-column" key={row[0]}>
                  <ul className="d-flex p-0 m-0">
                    {row.map((year) => {
                      // Check if the current year is the selected one
                      const isSelectedYear = shownYear === year;

                      // Determine background color based on state
                      const getBgColor = () => {
                        if (isSelectedYear) return "#216ba5"; // Blue
                        if (hoverYear === year)
                          return "var(--dm-surface-2, #f0f0f0)"; // Light gray
                        return "var(--dm-surface, white)"; // Default
                      };

                      return (
                        <li
                          key={year}
                          className="list-unstyled p-2 w-50"
                          style={{
                            cursor: "pointer",
                            backgroundColor: getBgColor(),
                            color: isSelectedYear
                              ? "white"
                              : "var(--dm-text, black)",
                            borderRadius: "0.3rem",
                          }}
                          onClick={() => {
                            changeYear(year);
                            setSelectYear(false);
                          }}
                          onMouseEnter={() => setHoverYear(year)}
                          onMouseLeave={() => setHoverYear(null)}
                        >
                          {year}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ))}
            </div>

            <span
              role="button"
              tabIndex={0}
              className="mt-2 px-2 pb-2 text-secondary"
              style={{ float: "right" }}
              onClick={handleToday}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleToday();
              }}
            >
              Today
            </span>
          </div>
        )}
      </div>

      {/* Go to Next Month */}
      <button
        onClick={increaseMonth}
        type="button"
        className="btn py-0 ms-1 rounded-0 border border-0"
        style={{ color: "var(--dm-text, #212529)" }}
        disabled={nextMonthButtonDisabled}
      >
        {">"}
      </button>
    </div>
  );
};

export default CustomDatePicker;
